import { inject, Injectable } from '@angular/core';
import type {
  ICaseItemDtoV2,
  IInventoryItemDetailed,
  IMarketplaceKitData,
  IP2pPurchaseItem,
  ISteamStoreInventory,
  IUserP2pPermissionInterface,
  Widgets,
} from '@dev-fast/types';
import { ModalNames } from '@dev-fast/types';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { StateReset } from 'ngxs-reset-plugin';
import type { Observable } from 'rxjs';

import { ChangeActiveWidgets, ToggleBackground } from '@app/core/state/layout';
import { OpenModal } from '@app/core/state/modals';
import { P2pBuyingState, P2pMarketState, ToggleSelected } from '@app/core/state/p2p';
import { UserState } from '@app/core/state/user-store';

import { GetAvailabilityItemInCases, GetItemAvailabilityOnThenMarket } from './skins.actions';
import { SkinsState } from './skins.state';

@Injectable({ providedIn: 'root' })
export class SkinsService {
  readonly #actions$ = inject(Actions);
  readonly #store = inject(Store);

  readonly itemDetails$: Observable<IInventoryItemDetailed | null> = this.#store.select(SkinsState.itemDetails);
  readonly isLoadingItemDetails$: Observable<boolean> = this.#store.select(SkinsState.isLoadingItemDetails);
  readonly itemAvailabilityOnThenMarket$: Observable<IMarketplaceKitData[] | null> = this.#store.select(
    SkinsState.itemAvailabilityOnThenMarket,
  );

  readonly itemAvailabilityInCases$: Observable<ICaseItemDtoV2[] | null> = this.#store.select(SkinsState.itemAvailabilityInCases);
  readonly purchasing$: Observable<IP2pPurchaseItem[]> = this.#store.select(P2pBuyingState.purchasing);
  readonly p2pPermissions$: Observable<IUserP2pPermissionInterface | null> = this.#store.select(UserState.p2pPermissions);

  readonly getMarketItemsAction$ = this.#actions$.pipe(ofActionSuccessful(GetItemAvailabilityOnThenMarket));

  toggleSelected = (value: IMarketplaceKitData): Observable<void> => this.#store.dispatch(new ToggleSelected(value));

  getItemAvailabilityInCases = (name: string): Observable<void> => this.#store.dispatch(new GetAvailabilityItemInCases({ name }));

  changeActiveWidgets = (widgets: Widgets[]): Observable<void> => this.#store.dispatch(new ChangeActiveWidgets(widgets));

  resetState = (): Observable<void> => this.#store.dispatch(new StateReset(SkinsState));

  resetP2pMarketState = (): Observable<void> => this.#store.dispatch(new StateReset(P2pMarketState));

  toggleBackground = (color?: string): Observable<void> => this.#store.dispatch(new ToggleBackground(color));

  showBatchDetailsModal = (item: IMarketplaceKitData | ISteamStoreInventory): Observable<void> =>
    this.#store.dispatch(new OpenModal(ModalNames.P2P_KIT_DETAILS, item));
}
